import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { ModalCard } from 'partial/components/Modal';

import AsyncButton from 'modules/common/components/AsyncButton';
import FormInput from 'partial/components/form/FormInput';
import FormSelect from 'partial/components/form/FormSelect';

import { useAddBannedWord, useEditBannedWord } from '../hooks';

const statusOptions = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const validationSchema = yup.object().shape({
  ban_word: yup
    .string()
    .min(5, 'Too short')
    .max(50, 'Too long')
    .required('Required'),
  status: yup.string().required('Required'),
});

function AddUpdate({ onClose, data, type }) {
  const [pending, setPending] = useState(false);
  const { values, setFieldValue, errors } = useFormik({
    initialValues: {
      ban_word: data?.ban_word || '',
      status: data?.status || null,
    },
    validationSchema,
    validateOnChange: false,
  });

  const isFieldEmpty = useMemo(
    () => !values?.ban_word.length || !values?.status,
    [values],
  );
  const onError = () => setPending(false);
  const onSuccess = () => {
    setPending(false);
    onClose();
  };

  const [isAddLoading, add] = useAddBannedWord({ onSuccess, onError });
  const [isEditLoading, edit] = useEditBannedWord({ onSuccess, onError });

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);

    if (type === 'add') add(values);
    if (type === 'edit') edit({ ...values, uuid: data?.uuid });
  };

  return (
    <ModalCard
      size="sm"
      title={`${type === 'add' ? 'ADD' : 'EDIT'} BANNED WORDS`}
      onClose={onClose}
    >
      <form className="px-4 py-6" onSubmit={handleSubmit}>
        <div className="space-y-6">
          <FormInput
            name="ban_word"
            onSetFieldValue={setFieldValue}
            value={values?.ban_word}
            error={errors.ban_word}
            label="Phrase"
            placeholder="Enter Phrase"
            type="text"
            required
          />
          <div className="space-y-1">
            <h4 className="capitalize font-semibold text-neutral-700">
              status <span className="text-danger-500/80">*</span>
            </h4>
            <FormSelect
              value={values?.status}
              error={errors?.status}
              name="status"
              placeHolder="Select status"
              onChange={setFieldValue}
              options={statusOptions}
            />
          </div>

          <div className="flex justify-end gap-2 mt-10">
            <AsyncButton
              color="outline"
              className="font-[400] !text-neutral-500"
              onClick={onClose}
            >
              Cancel
            </AsyncButton>
            <AsyncButton
              type="submit"
              className="font-[400]"
              color="outline"
              disabled={isFieldEmpty}
              isLoading={pending || isAddLoading || isEditLoading}
            >
              {type === 'add' ? 'save' : 'save changes'}
            </AsyncButton>
          </div>
        </div>
      </form>
    </ModalCard>
  );
}

AddUpdate.defaultProps = {
  type: 'add',
  data: {},
};

AddUpdate.propTypes = {
  onClose: PropTypes.func.isRequired,
  type: PropTypes.string,
  data: PropTypes.instanceOf(Object),
};

export default AddUpdate;
